import React, { useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useDrop } from 'react-dnd';
import * as _ from 'lodash-es';

import { Box, Grid, IconButton, Paper, Popover, Popper } from '@mui/material';
import { VirtualElement } from '@popperjs/core';

import * as GC from '@mescius/spread-sheets';
import * as GCD from '@mescius/spread-sheets-designer';
import { Designer } from '@mescius/spread-sheets-designer-react';
import * as ExcelIO from '@mescius/spread-excelio';

import { Header } from '../components/Header';
import { Sidebar } from '../components/Sidebar';
import { Alert } from '../components/Alert';
import { OverlayLoading } from '../components/OverlayLoading';
import { DeletePropertyMappingIcon, DeleteLoopIcon } from '../components/Icons';
import { Toast } from '../components/Toast';

import { useInit } from '../services/api/useInit';
import { useBusinessReportEditor } from '../services/store/Report';
import { useSpread } from '../services/store/Spread';
import { useMappingProperty } from '../services/store/Mapping';
import { useLoop } from '../services/store/Loop';
import { useError } from '../services/store/Error';
import { useConfirm } from '../contexts/Confirm';
import { useInfoDialog } from '../contexts/InfoDialog';

import { mappingAndLoopCommand } from '../utils/customCommands';
import { removeItemFromConfig } from '../utils/removeCommandFromConfig';
import { useStateWithGetter } from '../utils/useStateWithGetter';
import {
  getCellTagSafely,
  setCellTagSafely,
  updateCellTagSafely,
  isPropertyCell,
  isLoopEndTag,
  hasCellTagInRange,
} from '../utils/cellTag';
import {
  findContainingLoop,
  initializeLoopIdCounter,
  isCellInAnyLoop,
  isCellInLoopHeader,
  isCellOutsideLoopOrInLoopHeader,
  removeLoopById,
  findContainingLoopMappingProperties,
} from '../utils/loop';
import { removePropertyByCell, setPropertyByCell } from '../utils/property';
import {
  applyDefaultFont,
  applyLoopBackground,
  applyLoopHeaderBackground,
} from '../utils/style';
import { registerClipboardChangeAndPasteHandler } from '../utils/clipboardChangePasteHandler';
import { registerColumnChangeHandler } from '../utils/columnChangeHandler';
import { registerRowChangeHandler } from '../utils/rowChangeHandler';
import { registerDragDropHandler } from '../utils/dragDropHandler';
import { setupContextMenu } from '../utils/contextMenu';
import { useThrowError } from '../utils/useThrowError';
import { translate } from '../utils/translations';

import { PropertyView } from '../types';

import {
  ACTIVE_LOOP_HEADER_COLOR,
  COMMAND_MANAGER_REGISTER_PROPERTY,
  DISABLED_TAB,
  EXCEL_FONT,
  MAPPING_RESTRICTION,
  MAX_UPLOAD_CELL_SIZE,
  OPTION_CATEGORY_ID,
  WARNING_UPLOAD_CELL_SIZE,
  ZINDEX,
} from '../constants';

const baseConfig = _.cloneDeep(GCD.Spread.Sheets.Designer.DefaultConfig);

const initDesignerConfig = () => {
  /**
   * 不要タブの削除
   **/
  baseConfig.ribbon = (
    baseConfig.ribbon as GCD.Spread.Sheets.Designer.IRibbonPanel[]
  ).filter((tab) => {
    // ホームタブの『セル』リボン
    if (
      tab.id === 'home' &&
      !_.isUndefined(tab.buttonGroups[6].commandGroup.children)
    ) {
      tab.buttonGroups[6].commandGroup.children = (
        tab.buttonGroups[6].commandGroup
          .children as GCD.Spread.Sheets.Designer.ICommandGroup[]
      ).filter((command) => {
        if (
          (command.command === 'cellsInsert' ||
            command.command === 'cellsDelete') &&
          !_.isUndefined(command.children)
        ) {
          // 『セルの挿入』『セルの削除』『セパレート（横線）』を削除
          return _.pullAt(command.children, [0, 1]);
        }

        return command;
      });
    }
    return !DISABLED_TAB.includes(tab.id);
  });

  // ファイルタブの削除
  baseConfig.fileMenu = undefined;

  /**
   * 関数（fx）欄の削除
   **/
  if (baseConfig.sidePanels) {
    baseConfig.sidePanels = baseConfig.sidePanels.filter(
      (panel) => panel.command !== 'formulaBarPanel'
    );
  }

  /**
   * 使用できるフォントを限定する
   * ・MS ゴシック
   * ・MS 明朝
   * ・MS P ゴシック
   * ・MS P 明朝
   **/
  const fontFamilyCmd = GCD.Spread.Sheets.Designer.getCommand(
    'fontFamily'
  ) as GCD.Spread.Sheets.Designer.ICommand;
  const customCNFont = [
    { value: EXCEL_FONT.MS_GOTHIC, text: EXCEL_FONT.MS_GOTHIC },
    { value: EXCEL_FONT.MS_MINCHO, text: EXCEL_FONT.MS_MINCHO },
    { value: EXCEL_FONT.MS_P_GOTHIC, text: EXCEL_FONT.MS_P_GOTHIC },
    { value: EXCEL_FONT.MS_P_MINCHO, text: EXCEL_FONT.MS_P_MINCHO },
  ];
  fontFamilyCmd.dropdownList = customCNFont;
  fontFamilyCmd.commandName = 'customFontFamily';
  baseConfig.commandMap = {
    fontFamily: fontFamilyCmd,
  };

  // 拡大縮小印刷設定をリボンコンテナから削除
  removeItemFromConfig(
    baseConfig,
    'thumbnailClass',
    'ribbon-thumbnail-scale-to-fit'
  );

  return baseConfig;
};

const initializeSheet = (
  sheet: GC.Spread.Sheets.Worksheet,
  userUpload: boolean
) => {
  sheet.suspendPaint();
  const rowCount = sheet.getRowCount();
  const colCount = sheet.getColumnCount();

  // 保護されたシートの設定
  // 行列のリサイズ
  sheet.options.protectionOptions.allowResizeRows = true;
  sheet.options.protectionOptions.allowResizeColumns = true;
  // 行列の削除
  sheet.options.protectionOptions.allowDeleteRows = true;
  sheet.options.protectionOptions.allowDeleteColumns = true;
  // 行列の挿入
  sheet.options.protectionOptions.allowInsertRows = true;
  sheet.options.protectionOptions.allowInsertColumns = true;
  // 行列の非表示
  sheet.options.protectionOptions.allowOutlineRows = true;
  sheet.options.protectionOptions.allowOutlineColumns = true;

  //  ユーザーの独自フォーマットの場合はフォントをMSゴシックに統一する
  if (userUpload) {
    sheet
      .getRange(0, 0, rowCount, colCount)
      .locked(false)
      .font(EXCEL_FONT.MS_GOTHIC);
  }

  // NOTE: 「シートを保護」を利用するかどうかは検討中
  // sheet.options.isProtected = true;

  const style = new GC.Spread.Sheets.Style();
  style.locked = false;
  sheet.setDefaultStyle(style);

  // 数式を文字列として再設定
  for (let i = 0; i < sheet.getRowCount(); i++) {
    for (let j = 0; j < sheet.getColumnCount(); j++) {
      const formula = sheet.getFormula(i, j);
      if (formula) {
        sheet.setValue(i, j, formula);
        sheet.setFormula(i, j, null); //(3)数式を削除
      }
    }
  }

  // 拡大縮小印刷設定
  const pi = sheet.printInfo() as GC.Spread.Sheets.Print.PrintInfo;
  pi.fitPagesWide(1);
  pi.fitPagesTall(0);
  pi.zoomFactor(1);
  sheet.printInfo(pi);

  sheet.resumePaint();
};

const initializeSpread = (
  spread: GC.Spread.Sheets.Workbook,
  userUpload: boolean = false
) => {
  // 新規でシートを追加させないようにする
  // spread.options.newTabVisible = false;
  // シートタブを表示させないようにする
  // spread.options.tabStripVisible = false;
  // 数式の入力を無効にする
  spread.options.allowUserEditFormula = false;
  // 数式の入力を無効にする
  spread.options.calculationMode = GC.Spread.Sheets.CalculationMode.manual;
  // Undo/Redo処理を有効にする
  spread.options.allowUndo = true;
  // 読み込み以降にも自動で計算が実行されないようにする
  spread.options.calculationMode = GC.Spread.Sheets.CalculationMode.manual;

  // シートの設定
  _.forEach(spread.sheets, (sheet) => {
    initializeSheet(sheet, userUpload);
  });
};

/**
 * テンプレート編集画面
 * Excelテンプレートを編集する画面
 * テンプレートの内容をJSON形式で出力したりできる
 * @returns JSX.Element
 */
export function Report(): React.JSX.Element {
  const [spreadOpen, setSpreadOpen] = useState<boolean>(false);
  const { reportFormatFile, isInitialRequestComplete } =
    useBusinessReportEditor();
  const { mappingProperties } = useMappingProperty();
  const { loops } = useLoop();
  const {
    spread,
    setSpread,
    getIsDirtyToSpread,
    setIsDirtyToSpread,
    setIsDetail,
  } = useSpread();
  const confirm = useConfirm();
  const infoDialog = useInfoDialog();
  const { error, setError } = useError();
  const { throwError } = useThrowError();
  const [isInitialized, setIsInitialized] = useState(false);
  const [isCellMenuOpen, setIsCellMenuOpen] = useState(false);
  const [isLoopMenuOpen, setIsLoopMenuOpen] = useState(false);
  const [isDeleteButtonVisible, setIsDeleteButtonVisible] = useState(false);
  const [cellMenuAnchorPosition, setCellMenuAnchorPosition] =
    useState<VirtualElement>();
  const [loopCellMenuAnchorPosition, setLoopCellMenuAnchorPosition] =
    useState<VirtualElement>();
  const [isMappingRendered, setIsMappingRendered] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [, setFormatFileLoaded, getFormatFileLoaded] =
    useStateWithGetter(false);
  const [isOverSizeFileUpload, setIsOverSizeFileUpload] = useState(false);

  const ref = useRef<HTMLDivElement>(null);

  const designerConfig = initDesignerConfig();

  /**
   * Init処理
   */
  useInit();

  /**
   * SpreadJSのワークブックの初期化
   */
  const initWorkbook = (designer: GCD.Spread.Sheets.Designer.Designer) => {
    if (isInitialized) return;

    const spread = designer.getWorkbook() as GC.Spread.Sheets.Workbook;

    /**
     * コマンドの登録
     * ・ プロパティの紐付け、解除
     * ・ 範囲指定の作成、削除
     * ・ マッピングプロパティのクリップボード貼り付け
     */
    const commandManager = spread.commandManager();
    commandManager.register(
      COMMAND_MANAGER_REGISTER_PROPERTY.CREATE_PROPERTY_MAPPING,
      mappingAndLoopCommand
    );
    commandManager.register(
      COMMAND_MANAGER_REGISTER_PROPERTY.DELETE_PROPERTY_MAPPING,
      mappingAndLoopCommand
    );
    commandManager.register(
      COMMAND_MANAGER_REGISTER_PROPERTY.CREATE_LOOP,
      mappingAndLoopCommand
    );
    commandManager.register(
      COMMAND_MANAGER_REGISTER_PROPERTY.DELETE_LOOP,
      mappingAndLoopCommand
    );
    commandManager.register(
      COMMAND_MANAGER_REGISTER_PROPERTY.CREATE_PROPERTY_MAPPING_CLIPBOARD_PASTED,
      mappingAndLoopCommand
    );
    commandManager.register(
      COMMAND_MANAGER_REGISTER_PROPERTY.CREATE_LOOP_HEADER,
      mappingAndLoopCommand
    );

    /**
     * アクティブシートの変更時
     * ・数式の入力を無効にする
     */
    spread.bind(GC.Spread.Sheets.Events.SheetTabClick, () => {
      spread.options.allowUserEditFormula = false;
      spread.options.calculationMode = GC.Spread.Sheets.CalculationMode.manual;
    });

    /**
     * Popperの表示位置の調整
     * @param top : 上端の位置
     * @param left : 左端の位置
     */
    const createPopperPosition = (
      top: number,
      left: number
    ): VirtualElement => ({
      getBoundingClientRect: () => ({
        width: 0,
        height: 0,
        top: top,
        right: 0,
        bottom: 0,
        left: left,
        x: 0,
        y: 0,
        toJSON: () => undefined,
      }),
    });

    /**
     * セル選択変更時にポップオーバーを表示
     * @TODO : セルクリックイベントがあれば切り替えたい
     * @TODO : 表示位置の調整
     */
    spread.bind(
      GC.Spread.Sheets.Events.SelectionChanged,
      (e: unknown, data: unknown) => {
        // @ts-expect-error - a
        const { col, row } = data.newSelections[0];
        // @ts-expect-error - a
        const { sheet } = data;

        const hasPropertyMap = isPropertyCell(sheet, row, col);
        const hasLoop = findContainingLoop(sheet, row, col);

        if (!hasPropertyMap && !hasLoop) {
          setIsCellMenuOpen(false);
          setIsLoopMenuOpen(false);
          return;
        }
        const canvas = document.querySelector(
          'canvas[gcuielement="gcWorksheetCanvas"]'
        );

        if (!canvas) return;

        const canvasOffset = canvas.getBoundingClientRect();

        if (hasPropertyMap) {
          const rect = spread.getActiveSheet().getCellRect(row, col);

          // アイコンの位置をセルの左側に表示するため、アイコンのサイズ分20pxずらす
          setCellMenuAnchorPosition(
            createPopperPosition(
              rect.y + canvasOffset.top,
              rect.x + canvasOffset.left - 20
            )
          );
          setIsCellMenuOpen(true);
          setIsDeleteButtonVisible(true);
        } else {
          setIsCellMenuOpen(false);
        }

        if (hasLoop) {
          const rect = spread
            .getActiveSheet()
            .getCellRect(hasLoop.startRow, hasLoop.endCol);

          // アイコンの位置をセルの右側に表示するため、アイコンのサイズ分20pxずらす
          setLoopCellMenuAnchorPosition(
            createPopperPosition(
              rect.y + canvasOffset.top - 40,
              rect.width + rect.x + canvasOffset.left + 20
            )
          );
          setIsLoopMenuOpen(true);
          setIsDeleteButtonVisible(true);
        } else {
          setIsLoopMenuOpen(false);
        }
      }
    );

    /**
     * プロパティの紐づけられたセルと明細範囲内の編集を禁止する
     */
    spread.bind(
      GC.Spread.Sheets.Events.EditStarting,
      (sender: unknown, args: GC.Spread.Sheets.IEditStartingEventArgs) => {
        const { sheet, row, col } = args;

        if (
          isPropertyCell(sheet, row, col) ||
          isCellInAnyLoop(sheet, row, col)
        ) {
          args.cancel = true;
        }
      }
    );

    /**
     * 行に対する変更を実施した時のイベント
     */
    spread.bind(
      GC.Spread.Sheets.Events.RowChanged,
      (e: unknown, info: GC.Spread.Sheets.IRowChangedEventArgs) => {
        setDirtyFlag();
      }
    );

    /**
     * 列に対する変更を実施した時のイベント
     */
    spread.bind(
      GC.Spread.Sheets.Events.ColumnChanged,
      (e: unknown, info: GC.Spread.Sheets.IColumnChangedEventArgs) => {
        setDirtyFlag();
      }
    );

    /**
     * 行の幅を変更する時のイベント
     */
    spread.bind(
      GC.Spread.Sheets.Events.RowHeightChanged,
      (e: unknown, info: GC.Spread.Sheets.IRowHeightChangedEventArgs) => {
        setDirtyFlag();
      }
    );

    /**
     * 列の幅を変更する時のイベント
     */
    spread.bind(
      GC.Spread.Sheets.Events.ColumnWidthChanged,
      (e: unknown, info: GC.Spread.Sheets.IColumnWidthChangedEventArgs) => {
        setDirtyFlag();
      }
    );

    /**
     * ペースト時のイベント
     */
    spread.bind(
      GC.Spread.Sheets.Events.ClipboardPasting,
      (sender: unknown, args: GC.Spread.Sheets.IClipboardPastingEventArgs) => {
        const { sheet, cellRange } = args;
        // プロパティの紐づけられたセルと明細範囲内へのペーストを禁止する
        if (hasCellTagInRange(sheet, cellRange)) {
          args.cancel = true;
        }
      }
    );

    /**
     * ペースト後のイベント
     */
    spread.bind(
      GC.Spread.Sheets.Events.ClipboardPasted,
      (sender: unknown, args: GC.Spread.Sheets.IClipboardPastedEventArgs) => {
        const { sheet, cellRange } = args;
        if (!cellRange) return;

        // ペーストされたフォントが使用不可の場合はデフォルトのフォントに置き換える
        applyDefaultFont(sheet, cellRange);
      }
    );

    /**
     * シートが追加された時のイベント
     */
    spread.bind(
      GC.Spread.Sheets.Events.SheetChanged,
      (sender: unknown, args: GC.Spread.Sheets.ISheetChangedEventArgs) => {
        if (args.propertyName === 'insertSheet') {
          const sheet = spread.getSheet(args.sheetIndex);
          initializeSheet(sheet, false);
        }
      }
    );

    /**
     * シート名が変更された時のイベント
     */
    spread.bind(
      GC.Spread.Sheets.Events.SheetNameChanging,
      async (
        sender: unknown,
        args: GC.Spread.Sheets.ISheetNameChangingEventArgs
      ) => {
        if (args.newValue.length > 32) {
          args.cancel = true;
          const ok = await infoDialog({
            message: translate('ERROR.SHEET_NAME_OVER_LENGTH'),
          });
          if (ok) return;
        }
      }
    );

    registerClipboardChangeAndPasteHandler(spread, (message: string) =>
      setError({ title: '', message })
    );

    registerDragDropHandler(spread, confirm);
    registerColumnChangeHandler(spread, confirm);
    registerRowChangeHandler(spread, confirm);

    /**
     * シートに対して変更があった場合にDirtyフラグを立てる
     */
    const setDirtyFlag = () => {
      if (!getIsDirtyToSpread()) {
        setIsDirtyToSpread(true);
      }
    };

    /**
     * プロパティの紐づけられたセルのコンテンツをDeleteキーで削除できないようにする
     */
    const clearCmd = GC.Spread.Sheets.Commands.clear.execute;
    GC.Spread.Sheets.Commands.clear.execute = function (context, options) {
      // シートの取得
      const sheet = context.getSheetFromName(options.sheetName);
      // 選択範囲の取得
      const sels = sheet.getSelections();

      if (_.some(sels, (sel) => hasCellTagInRange(sheet, sel))) {
        return false;
      }

      return clearCmd.apply(this, [context, options]);
    };

    setupContextMenu(spread);

    setSpread(spread);
    setIsInitialized(true);
  };

  /**
   * オブジェクトのドロップ時の処理
   * ・プロパティの割り当て
   */
  const [, dropBox] = useDrop(
    () => ({
      accept: 'BOX',
      drop: (item: PropertyView, monitor) => {
        if (_.isEmpty(spread)) return;

        const sheet = spread.getActiveSheet();
        const commandManager = spread.commandManager();
        const offset = monitor.getClientOffset();
        const canvas = document.querySelector(
          'canvas[gcuielement="gcWorksheetCanvas"]'
        );

        if (_.isEmpty(canvas) || _.isEmpty(offset)) return;

        const canvasOffset = canvas.getBoundingClientRect();
        const { row, col } = sheet.hitTest(
          offset.x - canvasOffset.left,
          offset.y - canvasOffset.top
        );

        if (_.isUndefined(row) || _.isUndefined(col)) return;

        if (!isCellOutsideLoopOrInLoopHeader(sheet, row, col)) {
          setError({
            title: translate('ERROR.FAILED_TO_ASSIGN_PROPERTY'),
            message: translate(
              'ERROR.PROPERTIES_CANNOT_BE_ASSIGNED_BEYOND_SECOND_ROW'
            ),
          });
          return;
        }

        // 明細範囲外へ明細範囲専用項目を配置した場合、エラーとする
        if (
          item.mappingRestriction !== MAPPING_RESTRICTION.NOTHING &&
          !isCellInAnyLoop(sheet, row, col)
        ) {
          if (item.categoryId !== OPTION_CATEGORY_ID) {
            setError({
              title: translate('ERROR.CANNOT_SET_PROPERTY_TITLE'),
              message: translate('ERROR.CANNOT_SET_PROPERTY_OUTSIDE_LOOP'),
            });
            return;
          }
        }

        // 明細範囲内に同居できないカテゴリが存在するため、制限をかける
        if (item.mappingRestriction !== MAPPING_RESTRICTION.NOTHING) {
          const loopMappingProperties = findContainingLoopMappingProperties(
            sheet,
            row,
            col
          );
          // ドロップしたセルが明細範囲内にあるか確認（存在する場合はLoopIDで検索しているため、一意に定まる）
          if (loopMappingProperties.length > 0) {
            // 明細範囲内のプロパティが存在するかを確認する
            if (
              loopMappingProperties[0].mappingProperties &&
              loopMappingProperties[0].mappingProperties.length > 0
            ) {
              const mappingRestrictionOnlyProperties =
                loopMappingProperties[0].mappingProperties.filter(
                  (prop) => prop.mappingRestriction === MAPPING_RESTRICTION.ONLY
                );

              // すでに『他カテゴリ項目と明細範囲に同居不可』のプロパティが存在するか確認し、ドロップしたプロパティが同じカテゴリかを確認する
              if (
                mappingRestrictionOnlyProperties.length > 0 &&
                mappingRestrictionOnlyProperties[0].categoryId !==
                  item.categoryId
              ) {
                setError({
                  title: translate('ERROR.CANNOT_SET_PROPERTY_TITLE'),
                  message: translate('ERROR.CANNOT_SET_PROPERTY_IN_LOOP'),
                });
                return;
              }

              const mappingRestrictionAndOptionProperties =
                loopMappingProperties[0].mappingProperties.filter(
                  (prop) =>
                    prop.mappingRestriction === MAPPING_RESTRICTION.AND_OPTION
                );

              // すでに『明細範囲にオプション項目のみ同居可能』のプロパティが存在するか確認し、ドロップしたプロパティが同じカテゴリかを確認する
              if (
                mappingRestrictionAndOptionProperties.length > 0 &&
                item.categoryId !== OPTION_CATEGORY_ID
              ) {
                // すでに『明細範囲にオプション項目のみ同居可能』のプロパティが存在するため、ドロップした『マッピング制限』のプロパティが『ONLY』の場合、違うカテゴリの値が入っているためエラーとする
                if (item.mappingRestriction === MAPPING_RESTRICTION.ONLY) {
                  setError({
                    title: translate('ERROR.CANNOT_SET_PROPERTY_TITLE'),
                    message: translate('ERROR.CANNOT_SET_PROPERTY_IN_LOOP'),
                  });
                  return;
                }

                const otherOptionCategoryProperties =
                  mappingRestrictionAndOptionProperties.filter(
                    (prop) => prop.categoryId !== OPTION_CATEGORY_ID
                  );
                if (
                  otherOptionCategoryProperties.length > 0 &&
                  otherOptionCategoryProperties[0].categoryId !==
                    item.categoryId
                ) {
                  setError({
                    title: translate('ERROR.FAILED_TO_ASSIGN_PROPERTY'),
                    message: translate('ERROR.CANNOT_SET_PROPERTY_IN_LOOP'),
                  });
                  return;
                }
              }
            }
          }
        }

        sheet.setActiveCell(row, col);

        commandManager.execute({
          cmd: COMMAND_MANAGER_REGISTER_PROPERTY.CREATE_PROPERTY_MAPPING,
          sheetName: sheet.name(),
          applyFunc: () => {
            setPropertyByCell(sheet, row, col, item);
          },
          revertFunc: () => {},
        });

        return {
          spread: spread,
        };
      },
    }),
    [spread, loops]
  );
  dropBox(ref);

  /**
   * 帳票フォーマット読み込み時の処理
   */
  useEffect(() => {
    setIsDetail(false);
  }, []);

  /**
   * 帳票フォーマット読み込み時の処理
   */
  useEffect(() => {
    if (
      _.isNull(reportFormatFile) ||
      _.isNull(spread) ||
      getFormatFileLoaded()
    ) {
      return;
    }
    setFormatFileLoaded(true);
    const isUserUpload = reportFormatFile.source === 'upload';
    if (isUserUpload) {
      setSpreadOpen(false);
    }

    const excelIO = new ExcelIO.IO();
    excelIO.open(
      reportFormatFile.file,
      async (json: JSON) => {
        spread.fromJSON(json);

        const sheetCount = spread.getSheetCount();
        let cellCount = 0;
        for (let i = 0; i < sheetCount; i++) {
          const sheet = spread.getSheet(i);
          cellCount += sheet.getRowCount() * sheet.getColumnCount();
        }

        if (isUserUpload) {
          if (cellCount > MAX_UPLOAD_CELL_SIZE) {
            throwError({
              title: translate('ERROR.FAILED_TO_UPLOAD_FILE_TITLE'),
              message: translate('ERROR.FAILED_TO_UPLOAD_FILE_OVER_CELL_SIZE'),
            });
            return;
          } else if (cellCount > WARNING_UPLOAD_CELL_SIZE) {
            flushSync(() => {
              setIsOverSizeFileUpload(true);
            });
            await new Promise<void>((resolve) => {
              requestAnimationFrame(() => {
                requestAnimationFrame(() => resolve());
              });
            });
          }
        }

        initializeSpread(spread, isUserUpload);
        setSpreadOpen(true);
      },
      (e: unknown) => {
        setSpreadOpen(false);
        console.log(e);
        throwError({
          title: translate('ERROR.FAILED_TO_UPLOAD_FILE_TITLE'),
          message: translate('ERROR.THE_FILE_APPEARS_TO_BE_SPOOFED'),
        });
      }
    );
  }, [reportFormatFile, spread]);

  /**
   * マッピング情報がある場合はリストアされた情報を元にsheetを更新する
   */
  useEffect(() => {
    if (
      getFormatFileLoaded() &&
      spreadOpen &&
      !isMappingRendered &&
      isInitialRequestComplete
    ) {
      const targetSheets: number[] = [];

      if (!spread) return;

      // 描画の停止（描画を１つにまとめる）
      spread.suspendPaint();

      // ループの背景色設定とタグ設定
      _.forEach(loops, (loop) => {
        const sheet = spread.getSheet(loop.sheetId);

        if (_.isUndefined(sheet)) return;

        const { startColIndex, startRowIndex, endColIndex, endRowIndex } =
          loop.loopRange;

        applyLoopBackground(sheet, {
          startRow: startRowIndex,
          startCol: startColIndex,
          endRow: endRowIndex,
          endCol: endColIndex,
        });

        // 明細範囲の開始・終了セルにタグを付与
        setCellTagSafely(sheet, startRowIndex, startColIndex, {
          loop: { start: loop.loopId },
        });
        setCellTagSafely(sheet, endRowIndex, endColIndex, {
          loop: { end: loop.loopId },
        });

        // TODO: mappingEnableCellRangeがBFFの修正で返るようになればif文は不要
        if (loop.loopHeaderRange) {
          // 明細範囲のヘッダー行の復元
          const loopHeaderRanges = GC.Spread.Sheets.CalcEngine.formulaToRanges(
            sheet,
            loop.loopHeaderRange,
            0,
            0
          );
          const loopHeaderRange = (
            loopHeaderRanges[0] as { ranges: GC.Spread.Sheets.Range[] }
          ).ranges[0];

          // 明細範囲ヘッダーの終了セルのタグを更新
          updateCellTagSafely(
            sheet,
            loopHeaderRange.row + loopHeaderRange.rowCount - 1,
            loopHeaderRange.col + loopHeaderRange.colCount - 1,
            (prev) => ({
              ...prev,
              loop: {
                ...prev?.loop,
                headerEnd: loop.loopId,
              },
            })
          );

          applyLoopHeaderBackground(sheet, {
            startRow: loopHeaderRange.row,
            startCol: loopHeaderRange.col,
            headerEndRow: loopHeaderRange.row + loopHeaderRange.rowCount - 1,
            headerEndCol: loopHeaderRange.col + loopHeaderRange.colCount - 1,
          });
        }

        targetSheets.push(loop.sheetId);
      });

      // プロパティと紐づいたセルの設定
      _.forEach(mappingProperties, (prop) => {
        const sheet = spread.getSheet(prop.sheetId);

        if (_.isUndefined(sheet)) return;

        setPropertyByCell(sheet, prop.rowIndex, prop.colIndex, prop);

        targetSheets.push(prop.sheetId);
      });

      const sheetIds = new Set(targetSheets);
      _.forEach([...sheetIds], (id) => {
        // 初期のマッピング情報をisDirtyの対象から除外する
        spread.getSheet(id).clearPendingChanges();
        // 明細範囲のIDカウンターを初期化する
        initializeLoopIdCounter(spread.getSheet(id), loops);
      });

      // 描画の再開
      spread.resumePaint();

      setIsMappingRendered(true);
    }
  }, [
    isInitialRequestComplete,
    isMappingRendered,
    loops,
    mappingProperties,
    spread,
    spreadOpen,
  ]);

  useEffect(() => {
    if (spreadOpen && isMappingRendered) {
      setIsLoading(false);
    } else {
      setIsLoading(true);
    }
  }, [spreadOpen, isMappingRendered]);

  /**
   * Cellに割り当てられたプロパティの削除
   */
  const deleteCellProperty = () => {
    if (!spread) return;

    const sheet = spread.getActiveSheet();
    const row = sheet.getActiveRowIndex();
    const col = sheet.getActiveColumnIndex();

    if (!isPropertyCell(sheet, row, col)) return;

    const commandManager = spread.commandManager();

    commandManager.execute({
      cmd: COMMAND_MANAGER_REGISTER_PROPERTY.DELETE_PROPERTY_MAPPING,
      sheetName: sheet.name(),
      applyFunc: () => {
        const loop = findContainingLoop(sheet, row, col);
        if (loop) {
          if (isCellInLoopHeader(row, col, loop)) {
            // 明細行のヘッダー部分は、明細行の背景色と同じ色にする
            removePropertyByCell(sheet, row, col, {
              type: GC.Spread.Sheets.PatternType.darkDown,
              backgroundColor: ACTIVE_LOOP_HEADER_COLOR,
              patternColor: 'white',
            });
            return;
          }
        }

        removePropertyByCell(sheet, row, col);
      },
      revertFunc: () => {},
    });
  };

  /**
   * 明細範囲の削除
   */
  const deleteActiveLoop = () => {
    if (!spread) return;

    const sheet = spread.getActiveSheet();
    const row = sheet.getActiveRowIndex();
    const col = sheet.getActiveColumnIndex();
    const hasLoop = findContainingLoop(sheet, row, col);
    if (_.isEmpty(hasLoop)) return;

    const cellTag = getCellTagSafely(sheet, hasLoop?.endRow, hasLoop?.endCol);
    if (!isLoopEndTag(cellTag)) return;

    const loopId = cellTag?.loop?.end;
    if (!loopId) return;

    const commandManager = spread.commandManager();

    commandManager.execute({
      cmd: COMMAND_MANAGER_REGISTER_PROPERTY.DELETE_LOOP,
      sheetName: sheet.name(),
      applyFunc: () => {
        removeLoopById(sheet, loopId);
      },
      revertFunc: () => {},
    });
  };

  return (
    <>
      <Header />

      <Box
        sx={
          spreadOpen
            ? {
                position: 'relative',
                height: 'calc(100vh - 88px)',
              }
            : {
                position: 'relative',
                width: 0,
                height: 0,
                visibility: 'hidden',
                overflow: 'hidden',
              }
        }
      >
        <Grid container spacing={2} sx={{ height: '100%' }}>
          <Grid size="auto" sx={{ height: '100%' }}>
            <Sidebar
              initDataset={isInitialRequestComplete}
              setFormatFileLoaded={setFormatFileLoaded}
            />
          </Grid>
          <Grid size="grow" sx={{ height: '100%' }}>
            <Paper sx={{ paddingRight: 2, height: '100%' }}>
              <div ref={ref} role={'Dustbin'} style={{ height: '100%' }}>
                <Designer
                  config={designerConfig}
                  designerInitialized={initWorkbook}
                  styleInfo={{ width: '100%', height: '100%' }}
                ></Designer>
              </div>
            </Paper>
          </Grid>
        </Grid>
      </Box>

      {/* 『display : none』と『visibility: hidden』の代わりにopacity: 0を使用しないと、popperが表示されていてもスクロールできてしまい、ボタンが意図しない場所に表示される */}
      <Popover
        open={isDeleteButtonVisible}
        anchorPosition={{ top: 0, left: 0 }}
        anchorReference="anchorPosition"
        onClose={() => {
          setIsCellMenuOpen(false);
          setIsLoopMenuOpen(false);
          setIsDeleteButtonVisible(false);
        }}
        onTransitionExited={() => {
          spread?.focus(true);
        }}
        sx={{ opacity: 0 }}
      >
        <Popper
          open={isCellMenuOpen}
          anchorEl={cellMenuAnchorPosition}
          sx={{
            zIndex: ZINDEX.OVERLAY,
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
          }}
        >
          <Grid container direction="column">
            <Grid size="auto">
              <IconButton
                color="warning"
                onClick={() => {
                  deleteCellProperty();
                  setIsCellMenuOpen(false);
                }}
              >
                <DeletePropertyMappingIcon color="warning" fontSize="small" />
              </IconButton>
            </Grid>
          </Grid>
        </Popper>

        <Popper
          open={isLoopMenuOpen}
          anchorEl={loopCellMenuAnchorPosition}
          sx={{
            zIndex: ZINDEX.OVERLAY,
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
          }}
        >
          <Grid container direction="column">
            <Grid size="auto">
              <IconButton
                color="warning"
                onClick={() => {
                  deleteActiveLoop();
                  setIsLoopMenuOpen(false);
                  setIsDeleteButtonVisible(false);
                }}
              >
                <DeleteLoopIcon color="warning" fontSize="small" />
              </IconButton>
            </Grid>
          </Grid>
        </Popper>
      </Popover>

      <Alert
        open={!_.isNull(error)}
        handleClose={() => {
          setError(null);
          spread?.focus(true);
        }}
        message={error?.message ?? ''}
        title={error?.title ?? ''}
      />

      <Toast
        open={isOverSizeFileUpload}
        onClose={() => setIsOverSizeFileUpload(false)}
        message={translate('ERROR.WARNING_TO_UPLOAD_FILE')}
        severity="warning"
      />

      <OverlayLoading loading={isLoading} />
    </>
  );
}
